import { isLocalBackendPage } from '@/services/backend';
import { redactCapture, onCapabilityRoute } from '@/analytics/urlRedact';
import type { CaptureResult, PostHogConfig } from 'posthog-js';
import { inConsentRegion, zoneCountry, browserTimeZone } from '@/analytics/consentRegion';
import { isRemoteOptedOut } from '@/analytics/privacy';

type Properties = Record<string, unknown>;

interface AnalyticsClient {
    capture(event: string, properties?: Properties): void;
    identify(id: string, properties?: Properties): void;
    register(properties: Properties): void;
    reset(): void;
    readonly config: Partial<PostHogConfig>;
    set_config(config: Partial<PostHogConfig>): void;
    startSessionRecording(): void;
    stopSessionRecording(): void;
}

// Client-side (public) PostHog project key; env override for parity with the rest
// of the fleet, falling back to the literal so a missing env var never dark-ships.
const POSTHOG_KEY =
    (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ||
    'phc_VaylrT0tbh4vTJhZ9GzZLr7aeFzJFrnZVqnFBkI7HMe';

/** Replay: text masked under these (ancestors count). */
export const REPLAY_MASK_TEXT = [
    '.ph-mask',
    '[data-testid="markup-selection-root"]',
    '.markup-layer-tree',
    '.breadcrumb',
].join(', ');
/** Replay: elements replaced by a placeholder (attributes carry a file name). */
export const REPLAY_BLOCK = '.ph-no-capture, .compare-file-name';

/**
 * The PostHog client config. NO consent banner anywhere (2026-10); what is
 * collected depends on the visitor's region (analytics/consentRegion.ts):
 *
 * CONSENT REGION (EU/EEA, UK, CH -- ePrivacy Art. 5(3) / GDPR / PECR / FADP make
 * device storage for analytics opt-in, and a persistent id / recording personal
 * data) -- the cookieless tier:
 *   - `cookieless_mode: 'always'`: nothing in cookies, localStorage or
 *     sessionStorage; users counted by a daily-rotating hash computed on
 *     PostHog's servers (project setting "cookieless server hash mode" must stay
 *     ON, else PostHog DROPS these events);
 *   - `person_profiles: 'never'` (identify() is a no-op): no persistent id;
 *   - no session replay, no heatmaps.
 *
 * EVERYWHERE ELSE -- full product analytics: PostHog's default persistence,
 * person profiles for identified (signed-in) users, session replay (masked
 * below), heatmaps.
 *
 * Both: console logs never captured; autocapture keeps element names and
 * attributes but no element TEXT (a markup's text is the user's note); every
 * URL redacted (file names, share tokens, fragments, query values --
 * urlRedact.ts) and NOTHING sent from a capability route (/share/*, /join:
 * the URL is a credential; see initAnalytics); the project discards client IPs
 * after GeoIP (country/city stay); `consent_tier` rides on every event.
 * Replay for the consent region would need a banner: cookieless_mode
 * 'on_reject' + opt_in_capturing().
 */
export function posthogConfig(restricted: boolean): Partial<PostHogConfig> {
    const common: Partial<PostHogConfig> = {
        api_host: 'https://pdfmarkuptool.com/ingest',
        ui_host: 'https://us.posthog.com',
        defaults: '2025-11-30',
        enable_recording_console_log: false,
        mask_all_text: true,
        // File names and credentials never leave the browser: the local
        // viewer's URL is /local/<file name>, a share link /share/<token>, a
        // pairing link /join#<code>, and PostHog stamps the URL on every event.
        // While the page is ON a capability route (reached by SPA navigation
        // after PostHog loaded) nothing is sent at all.
        before_send: (cr: CaptureResult | null) => (onCapabilityRoute() ? null : redactCapture(cr)),
    };
    if (restricted) {
        return {
            ...common,
            cookieless_mode: 'always',
            person_profiles: 'never',
            disable_session_recording: true,
            enable_heatmaps: false,
        };
    }
    return {
        ...common,
        person_profiles: 'identified_only',
        enable_heatmaps: true,
        // Session replay is disproportionately valuable at low N (watch why a
        // tester stalled). The PDF itself is a <canvas> (not recorded); the
        // markups (an SVG: their text is the user's notes), the markup tree and
        // every file name ARE DOM text, so replay masks them, and a file-name
        // element is blocked outright (its title attribute carries the name).
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: REPLAY_MASK_TEXT,
            blockSelector: REPLAY_BLOCK,
        },
    };
}

/** Persist the opt-in internal marker before route handling can discard the query. */
function persistInternalFlagFromQuery(): void {
    if (typeof window === 'undefined' || inConsentRegion()) return;
    try {
        const p = new URLSearchParams(window.location.search).get('pmtInternal');
        if (p === '1') window.localStorage.setItem('pmt.internal', '1');
        if (p === '0') window.localStorage.removeItem('pmt.internal');
    } catch {
        // Storage can be unavailable; analytics remains usable without the marker.
    }
}

persistInternalFlagFromQuery();

/** Our own devices: `?pmtInternal=1` once marks this browser internal (outside
 *  the consent region only -- it is stored), so analysis can drop it now that
 *  PostHog discards IPs. `?pmtInternal=0` clears it. */
function isInternal(): boolean {
    if (inConsentRegion() || typeof window === 'undefined') return false;
    try {
        return window.localStorage.getItem('pmt.internal') === '1';
    } catch {
        return false;
    }
}

let clientPromise: Promise<AnalyticsClient | null> | null = null;

// Production build (import.meta.env.PROD) is a BUILD-time flag: it is true on
// Firebase preview-channel builds (*.web.app) too, which the QA/e2e smoke runs
// hit -- so those sessions were polluting PostHog. Also key on the runtime
// hostname so only real production traffic (pdfmarkuptool.com / www.) is sent.
const isProductionHost =
    typeof window !== 'undefined' &&
    /(^|\.)pdfmarkuptool\.com$/.test(window.location.hostname);

export function initAnalytics(): Promise<AnalyticsClient | null> {
    // ...and never on a LOCAL review session (services/backend.ts isLocalBackendPage): its drawings
    // and markups stay on the LAN, and so does everything else the page sends.
    if (import.meta.env.SSR || !import.meta.env.PROD || !isProductionHost || isLocalBackendPage())
        return Promise.resolve(null);
    // An opted-out visitor (Do Not Track, Global Privacy Control, pmt_opt_out)
    // gets NO PostHog at all -- not just no track() events: autocapture and
    // pageviews would otherwise still run.
    if (isRemoteOptedOut()) return Promise.resolve(null);
    // A capability route (/share/<token>, /join#<pairing code>: the URL IS the
    // credential -- audit H05) never loads PostHog: no pageview, autocapture,
    // replay or heatmap can see it. Not cached, so an SPA navigation to an
    // ordinary route (e.g. /join -> the viewer, which drops the code) loads it then.
    if (!clientPromise && onCapabilityRoute()) return Promise.resolve(null);
    if (!clientPromise) {
        clientPromise = import('posthog-js').then(({ default: posthog }) => {
            const restricted = inConsentRegion();
            posthog.init(POSTHOG_KEY, posthogConfig(restricted));
            posthog.register({
                property: 'pdfmarkuptool.com',
                consent_tier: restricted ? 'cookieless' : 'full',
                // Cookieless events lose GeoIP (PostHog strips the IP first);
                // the time zone's country stands in. Full tier keeps GeoIP.
                ...(restricted ? { tz_country: zoneCountry(browserTimeZone()) } : {}),
                ...(isInternal() ? { is_internal: true } : {}),
            });
            return posthog as unknown as AnalyticsClient;
        });
    }
    return clientPromise;
}

/** What analyticsRouteChanged switched off on entering a capability route. */
let pausedConfig: Partial<PostHogConfig> | null = null;

/**
 * Call on every route change. PostHog that is ALREADY running when the page
 * navigates onto a capability route stops replay, autocapture, heatmaps and
 * dead-click capture there (before_send also drops every event while on it),
 * and gets them back on leaving. Not loaded yet -> nothing to do.
 */
export function analyticsRouteChanged(): void {
    if (!clientPromise) return;
    const capability = onCapabilityRoute();
    if (capability === (pausedConfig !== null)) return;
    void clientPromise.then((ph) => {
        if (!ph) return;
        if (capability && !pausedConfig) {
            pausedConfig = {
                autocapture: ph.config.autocapture,
                enable_heatmaps: ph.config.enable_heatmaps,
                capture_dead_clicks: ph.config.capture_dead_clicks,
                disable_session_recording: ph.config.disable_session_recording,
            };
            ph.stopSessionRecording();
            ph.set_config({ autocapture: false, enable_heatmaps: false, capture_dead_clicks: false });
        } else if (!capability && pausedConfig) {
            const restore = pausedConfig;
            pausedConfig = null;
            ph.set_config({
                autocapture: restore.autocapture,
                enable_heatmaps: restore.enable_heatmaps,
                capture_dead_clicks: restore.capture_dead_clicks,
            });
            if (!restore.disable_session_recording) ph.startSessionRecording();
        }
    });
}

export const analytics = {
    capture(event: string, properties?: Properties) {
        if (onCapabilityRoute()) return;
        void initAnalytics().then(client => client?.capture(event, properties));
    },
    identify(id: string, properties?: Properties) {
        void initAnalytics().then(client => client?.identify(id, properties));
    },
    register(properties: Properties) {
        void initAnalytics().then(client => client?.register(properties));
    },
    reset() {
        void initAnalytics().then(client => client?.reset());
    },
};
